"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  MessageSquare,
  Send,
  Sparkles,
  RefreshCw,
  Phone,
  Video,
  MoreVertical,
  CheckCheck,
  Lock,
  ArrowUpRight,
  Bot,
  Copy,
  Check,
  Building2,
  UtensilsCrossed,
  Dumbbell,
  Stethoscope,
  ShieldCheck,
  Headphones,
  Briefcase,
  Scale,
  Calculator,
} from "lucide-react";

interface ChatMessage {
  id: string;
  sender: "user" | "bot";
  text: string;
  time: string;
  detectedService?: string;
}

const PRESET_QUICK_ACTIONS = [
  {
    label: "🏢 Demo About Johnson Softwares",
    query: "Demo about Johnson Softwares",
    icon: Building2,
    badge: "Master Demo",
  },
  {
    label: "🏡 Real Estate Demo",
    query: "Real Estate Demo",
    icon: Building2,
    badge: "Most Popular",
  },
  {
    label: "🍽️ Restaurant Demo",
    query: "Restaurant Demo",
    icon: UtensilsCrossed,
  },
  {
    label: "🏋️ Gym & Fitness Demo",
    query: "Gym Demo",
    icon: Dumbbell,
  },
  {
    label: "🦷 Dental Clinic Demo",
    query: "Dental Demo",
    icon: Stethoscope,
  },
  {
    label: "📞 Telecalling Demo",
    query: "Telecalling Demo",
    icon: Headphones,
  },
  {
    label: "🛡️ Insurance Demo",
    query: "Insurance Demo",
    icon: ShieldCheck,
  },
  {
    label: "💼 Staffing & Hiring Demo",
    query: "Hiring Demo",
    icon: Briefcase,
  },
  {
    label: "⚖️ Legal Practice Demo",
    query: "Legal Demo",
    icon: Scale,
  },
  {
    label: "💰 Cost Estimator",
    query: "Instant Project Cost Estimator",
    icon: Calculator,
  },
];

const INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: "init-1",
    sender: "bot",
    text:
      "👋 *Hello! Welcome to Johnson Softwares.*\n" +
      "_Bespoke Enterprise Software • 100% Owned IP • Zero Monthly SaaS Tax • 72h Deployment_\n\n" +
      "We engineer dedicated CRM pipelines and sub-15s WhatsApp automation across 8 core industries.\n\n" +
      "💡 *Try mentioning:*\n" +
      "• *'Demo about Johnson Softwares'* ➡️ Complete catalog of 8 demos\n" +
      "• *'Real Estate'* ➡️ Speed-to-lead & 5-stage property pipeline\n" +
      "• Or ask for Restaurant, Gym, Dental, Insurance, Telecalling, Hiring, or Legal!\n\n" +
      "_Click any preset prompt below or type your question directly!_",
    time: "Just now",
    detectedService: "Johnson Softwares Welcome",
  },
];

export default function WhatsAppInteractiveSimulator() {
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [inputQuery, setInputQuery] = useState("");
  const [activeIndustry, setActiveIndustry] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior,
      });
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    scrollToBottom("smooth");
  }, [messages, isLoading]);

  const handleSendMessage = async (textToSend?: string) => {
    const query = (textToSend || inputQuery).trim();
    if (!query || isLoading) return;

    const userTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: query,
      time: userTime,
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputQuery("");
    setIsLoading(true);

    try {
      const res = await fetch("/api/whatsapp/simulate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: query,
          name: "VIP Prospect",
          activeIndustry,
        }),
      });

      const data = await res.json();
      const botTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

      if (data.success && data.text) {
        const ds = (data.detectedService || "").toLowerCase();
        if (ds.includes("gym") || ds.includes("fitness")) {
          setActiveIndustry("gym");
        } else if (ds.includes("restaurant") || ds.includes("dining")) {
          setActiveIndustry("restaurant");
        } else if (ds.includes("real estate")) {
          setActiveIndustry("real_estate");
        } else if (ds.includes("dental")) {
          setActiveIndustry("dental");
        } else if (ds.includes("telecall")) {
          setActiveIndustry("telecalling");
        } else if (ds.includes("master") || ds.includes("all demos")) {
          setActiveIndustry("");
        }

        setMessages((prev) => [
          ...prev,
          {
            id: `bot-${Date.now()}`,
            sender: "bot",
            text: data.text,
            time: botTime,
            detectedService: data.detectedService,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: `bot-${Date.now()}`,
            sender: "bot",
            text: "Sorry, we encountered a momentary delay connecting to the simulated WhatsApp engine. Please try again!",
            time: botTime,
          },
        ]);
      }
    } catch (err) {
      console.error("[Simulator Error]", err);
      const botTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          sender: "bot",
          text: "Network error connecting to the chatbot API. Please check your connection and retry.",
          time: botTime,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setMessages(INITIAL_MESSAGES);
    setInputQuery("");
    setActiveIndustry("");
  };

  const copyToClipboard = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Helper to render WhatsApp formatted text with bold (*...*) and clickable URLs
  const renderFormattedText = (rawText: string) => {
    const lines = rawText.split("\n");
    return lines.map((line, lIdx) => {
      // Parse markdown bold and URLs in this line
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = line.split(urlRegex);

      return (
        <div key={lIdx} className={line === "" ? "h-2.5" : "min-h-[1.25rem] leading-relaxed"}>
          {parts.map((part, pIdx) => {
            if (part.match(urlRegex)) {
              return (
                <a
                  key={pIdx}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 underline font-medium break-all inline-flex items-center gap-0.5"
                >
                  {part}
                  <ArrowUpRight className="w-3 h-3 inline-block shrink-0" />
                </a>
              );
            }

            // Bold parsing *bold*
            const boldParts = part.split(/(\*[^*]+\*)/g);
            return (
              <span key={pIdx}>
                {boldParts.map((bPart, bIdx) => {
                  if (bPart.startsWith("*") && bPart.endsWith("*") && bPart.length > 2) {
                    return (
                      <strong key={bIdx} className="font-bold text-white">
                        {bPart.slice(1, -1)}
                      </strong>
                    );
                  }
                  if (bPart.startsWith("_") && bPart.endsWith("_") && bPart.length > 2) {
                    return (
                      <em key={bIdx} className="italic text-slate-300">
                        {bPart.slice(1, -1)}
                      </em>
                    );
                  }
                  return <span key={bIdx}>{bPart}</span>;
                })}
              </span>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div className="w-full max-w-4xl mx-auto my-12">
      {/* Header & Context */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold tracking-wider uppercase mb-3 shadow-lg shadow-emerald-500/5">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Live WhatsApp Bot Simulator</span>
        </div>
        <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight font-heading">
          Test the Complete{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">
            WhatsApp Demo Suite
          </span>
        </h2>
        <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl mx-auto">
          Experience exactly how our sub-15s autonomous WhatsApp bot responds when prospects ask for a company demo, real estate details, or any of our 8 bespoke CRM verticals.
        </p>
      </div>

      {/* Quick Action Pills Carousel */}
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5 flex items-center justify-between">
          <span>⚡ Click to test instant demo queries:</span>
          <button
            onClick={handleReset}
            className="text-[11px] text-slate-400 hover:text-white flex items-center gap-1 transition-colors"
            title="Reset Chat"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Reset Demo</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESET_QUICK_ACTIONS.map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={() => handleSendMessage(item.query)}
                disabled={isLoading}
                className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900/90 hover:bg-emerald-950/50 border border-slate-800 hover:border-emerald-500/50 text-xs text-slate-200 hover:text-emerald-300 font-medium transition-all active:scale-95 shadow-sm disabled:opacity-50"
              >
                <Icon className="w-3.5 h-3.5 text-emerald-400 group-hover:scale-110 transition-transform" />
                <span>{item.label}</span>
                {item.badge && (
                  <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 ml-0.5">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Smartphone Frame */}
      <div className="relative rounded-3xl sm:rounded-[36px] bg-slate-950 border-4 border-slate-800 shadow-2xl overflow-hidden shadow-emerald-950/20">
        {/* Phone Notch & Status Bar */}
        <div className="bg-emerald-900/90 px-4 py-2 flex items-center justify-between text-[11px] text-emerald-100 font-medium border-b border-emerald-800/60">
          <div className="flex items-center gap-1.5">
            <span className="font-bold">9:41</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] tracking-wide uppercase font-semibold">Meta WhatsApp Cloud API Live</span>
          </div>
        </div>

        {/* WhatsApp App Top Bar */}
        <div className="bg-gradient-to-r from-emerald-800 to-teal-900 px-4 py-3 flex items-center justify-between text-white border-b border-emerald-700/50 shadow-md">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center font-black text-sm text-white shadow-md border-2 border-emerald-400/30">
                JS
              </div>
              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-emerald-900" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="font-heading font-bold text-sm tracking-tight text-white">
                  Johnson Softwares
                </h3>
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-400 text-slate-950 text-[9px] font-black" title="Verified Business">
                  ✓
                </span>
              </div>
              <p className="text-[11px] text-emerald-200/90 flex items-center gap-1">
                <span>Solutions Architecture • Online</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-emerald-200">
            <a
              href="tel:+919880893416"
              className="p-1.5 hover:text-white hover:bg-emerald-700/50 rounded-full transition-colors"
              title="Call Founder Johnson"
            >
              <Phone className="w-4 h-4" />
            </a>
            <a
              href="https://wa.me/919880893416"
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 hover:text-white hover:bg-emerald-700/50 rounded-full transition-colors"
              title="Open Official WhatsApp"
            >
              <Video className="w-4 h-4" />
            </a>
            <button
              onClick={handleReset}
              className="p-1.5 hover:text-white hover:bg-emerald-700/50 rounded-full transition-colors"
              title="Reset Chat"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* WhatsApp Chat Conversation Area */}
        <div 
          ref={chatContainerRef}
          className="h-[480px] sm:h-[540px] overflow-y-auto p-4 space-y-4 bg-slate-900/95 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:16px_16px]"
        >
          {/* Security E2E Notice */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-[10px] max-w-sm text-center font-medium shadow-sm">
              <Lock className="w-3 h-3 text-amber-400 shrink-0" />
              <span>Official 24/7 Cloud API • End-to-End Enterprise Encryption</span>
            </div>
          </div>

          {/* Messages Stream */}
          {messages.map((msg, idx) => {
            const isBot = msg.sender === "bot";
            return (
              <div
                key={msg.id}
                className={`flex flex-col ${isBot ? "items-start" : "items-end"} group`}
              >
                {/* Detected Service Tag for Bot */}
                {isBot && msg.detectedService && (
                  <div className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-bold uppercase tracking-wider mb-1 ml-1">
                    <Bot className="w-3 h-3" />
                    <span>{msg.detectedService}</span>
                  </div>
                )}

                {/* Bubble */}
                <div
                  className={`relative max-w-[90%] sm:max-w-[82%] rounded-2xl p-3.5 text-xs shadow-md transition-all ${
                    isBot
                      ? "bg-slate-800 text-slate-100 rounded-tl-sm border border-slate-700/80"
                      : "bg-emerald-700 text-white rounded-tr-sm border border-emerald-600/50"
                  }`}
                >
                  <div className="prose-sm leading-relaxed whitespace-pre-wrap">
                    {renderFormattedText(msg.text)}
                  </div>

                  {/* Bubble Footer with Timestamp & Status */}
                  <div className="mt-2 flex items-center justify-end gap-1.5 text-[10px] text-slate-400">
                    <span>{msg.time}</span>
                    {!isBot && (
                      <CheckCheck className="w-3.5 h-3.5 text-cyan-300 inline" />
                    )}
                    {isBot && (
                      <button
                        onClick={() => copyToClipboard(msg.text, idx)}
                        className="ml-2 text-slate-400 hover:text-white opacity-60 hover:opacity-100 transition-opacity"
                        title="Copy message"
                      >
                        {copiedIndex === idx ? (
                          <Check className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Typing Indicator */}
          {isLoading && (
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-medium ml-1">
              <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700">
                <Bot className="w-3.5 h-3.5 animate-spin" />
              </div>
              <span className="flex items-center gap-1">
                Johnson Softwares is typing
                <span className="animate-pulse">...</span>
              </span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* WhatsApp Chat Input Bar */}
        <div className="p-3 bg-slate-900 border-t border-slate-800 flex items-center gap-2">
          <input
            type="text"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Type 'demo about johnson softwares' or 'real estate'..."
            className="flex-1 bg-slate-950 border border-slate-800 rounded-full px-4 py-2.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
            disabled={isLoading}
          />

          <button
            onClick={() => handleSendMessage()}
            disabled={!inputQuery.trim() || isLoading}
            className="w-10 h-10 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 shadow-lg shadow-emerald-600/30 shrink-0"
            aria-label="Send message"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Footer CTA & Real WhatsApp Link */}
      <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400 px-2">
        <div className="flex items-center gap-2 text-emerald-400">
          <ShieldCheck className="w-4 h-4" />
          <span>Real Meta Webhook: Send these exact phrases directly to our WhatsApp number!</span>
        </div>
        <a
          href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares%2C%20I%27d%20like%20a%20demo%20of%20your%20software%20and%20CRMs"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-white hover:text-emerald-400 font-bold bg-emerald-600/20 border border-emerald-500/40 px-3 py-1.5 rounded-lg hover:bg-emerald-600/30 transition-all"
        >
          <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
          <span>Open in Real WhatsApp (+91 98808 93416)</span>
          <ArrowUpRight className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
